import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { environment } from '../../../environments/environment';
import { CVA_AUTH_MODULES } from '../../models/auth.modules.enum';
import { CVA_CONSTANTS } from '../../util/constants';
import { AppointmentService } from "../../services/appointment/appointment.service";
import { CVANotificationService } from '../../services/cva-notification/cva-notification.service';
import { NotificationClassEnum } from '../../models/notification-class.enum';
import { ROUTE_CONSTANTS } from '../../util/route.constants';
import { Router } from '@angular/router';
import { SpeechToTextService } from '@privatelibdev/speech-to-text';
import { SoapServiceService } from '../../services/soap-service/soap-service.service';
import { TimerService } from "../../services/timer/timer.service";
import { PractitionerService } from '../../services/practitioner/practitioner.service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { BottomSheetComponent } from '../bottom-sheet/bottom-sheet.component';
import { ITab, TemplateListing } from '../../models/manage-template.interface';
import { FormControl } from '@angular/forms';
import { SessionInactivityService } from '../../services/session-inactivity/session-inactivity.service';
import { firstValueFrom } from 'rxjs';
import { ConsentService } from '../../services/patient-consent/consent.service';
import { UserPreferenceService } from '../../services/user-preferences/user-preference.service';
import { ConsultationRecommendationService } from '../../services/consultation-recommendation/consultation-recommendation.service';
import { HistoricalDataService } from '../../services/historical-data/historical-data.service';
import { VerticalUserInputs } from '../../models/dictation.interface';
import { CdmHealthScreeningRow, CdmPlanRow, CdmPlanService, CdmSourceData } from '../../services/cdm-plan/cdm-plan.service';
import { IAppointment } from '../../models/appointment.interface';
import { resolveOverlayStatus, SpeechProvider, SttUIStatus } from '../../models/stt-chunked.model';
import { ScribeService } from '../../services/stt-chunked/scribe.service';
import { GenerateButtonComponent } from '../../shared/generate-button/generate-button.component';

@Component({
  standalone: false,
  selector: 'cva-record-transcription',
  templateUrl: './record-transcription.component.html',
  styleUrl: './record-transcription.component.scss',

})
export class RecordTranscriptionComponent implements OnDestroy, OnInit, OnChanges {
  @ViewChild('textareaRef') textareaRef!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('speechHost') speechHost?: ElementRef<HTMLElement>;
  @ViewChild('generateMoreBtn') generateMoreBtn!: GenerateButtonComponent;

  @Input() isViewMode: boolean = false;
  @Input() appointmentId!: string;
  @Input() appointmentRefId!: string;
  @Input() patientId: string = '';
  @Input() transcription: string = '';
  @Input() DEFAULT_PREFERRED_TEMPLATE: string;
  @Input() isTabActive: boolean = true;
  @Input() hideLeftSection: boolean = false;
  @Input() hasDefaultTemplate: boolean;
  @Input() noteTemplateListing: TemplateListing[] = [];
  @Input() control: FormControl;
  @Input() vitals: VerticalUserInputs[] = [];
  @Input() userConsentTaken: boolean = false;
  @Input() liveTranscriptionEnabled: boolean | null = null;
  @Output() switchToEditMode: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() updateTabIndex: EventEmitter<ITab> = new EventEmitter<ITab>()
  @Output() toggleTabDisable = new EventEmitter()
  @Output() transcriptionChange = new EventEmitter<string>();
  @Output() recordingStateChange = new EventEmitter<boolean>();

  cvaUI_url = environment.cvaUI_url;
  CVA_AUTH_MODULES = CVA_AUTH_MODULES;
  CVA_CONSTANTS = CVA_CONSTANTS;
  editableTranscript = environment.editableTranscripts;
  recordingStarted = false;
  currentDate: Date = new Date();
  lineBreakPattern: string = '';
  hasNewLineAppended: boolean = false;
  PRIVACY_POLICY_ROUTE = ROUTE_CONSTANTS.PRIVACY_POLICY;
  textAreaPlaceHolder = CVA_CONSTANTS.MESSAGE.TEXTAREAR_PLACEHOLDER;
  toggleState: boolean = false;
  referenceId: string | undefined;
  isLIC = localStorage.getItem('isLIC') === "true";

  patientConsentSetting: boolean = true;
  patientHistory: number = 0;

  selectedSpeechProvider: SpeechProvider = environment.sttService.defaultProvider;

  private readonly isBottomSheetEnabled = false;
  private cdmLoadedPatientId: string = '';
  private stickToBottom = true;

  cdmSourceData: CdmSourceData = {
    consultations: [],
    allergies: [],
    medications: [],
  };
  isCdmSourceLoading: boolean = false;
  isCdmPlanLoading: boolean = false;
  cdmPlanError: string = '';
  cdmPlanResponse: unknown = null;
  cdmEligibility: string = '';
  cdmEligibilityReason: string = '';
  cdmAnnualSmartGoal: string = '—';
  cdmPlanRows: CdmPlanRow[] = [];
  cdmHealthScreeningRows: CdmHealthScreeningRow[] = [];
  cdmPatientName: string = '';
  cdmPatientDob: string = '';
  cdmPatientMrn: string = '';
  cdmPatientGender: string = '';
  cdmPatientId: string = '';

  sttUIStatus: SttUIStatus = '';
  startDictationLabel = 'Start Dictation';
  stopDictationLabel = 'Stop Dictation';

  constructor(
    private readonly router: Router,
    private readonly appointmentService: AppointmentService,
    private readonly cvaNotificationService: CVANotificationService,
    public speechToTextService: SpeechToTextService,
    private readonly soapServiceService: SoapServiceService,
    private readonly timerService: TimerService,
    public practitionerService: PractitionerService,
    private readonly bottomSheet: MatBottomSheet,
    private readonly dialog: MatDialog,
    private readonly sessionInactivityService: SessionInactivityService,
    private readonly consentService: ConsentService,
    private readonly userPreferenceService: UserPreferenceService,
    private readonly consultationRecommendationService: ConsultationRecommendationService,
    private readonly historicalDataService: HistoricalDataService,
    private readonly cdmPlanService: CdmPlanService,
    private readonly scribeService: ScribeService,
    private cdr: ChangeDetectorRef
  ) {
    this.transcription = this.transcription || "";
    console.log("Record transcription page loaded  &  STT with speech provider: " + this.selectedSpeechProvider);
  }

  ngOnInit() {
    if (this.isMobileView() && !this.hideLeftSection && !this.isLIC) {
      this.openBottomSheet();
    }

    if (this.isLIC) {
      const currentAppointment = this.soapServiceService.getAppointmentAndDraftedNotes();
      if (currentAppointment) {
        this.transcription = currentAppointment.transcription;
      }
    }
    const currentAppointment = this.soapServiceService.getCurrentAppointment();
    const historyAppointment = history?.state?.appointment as Partial<IAppointment> | undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const appointmentInfo = JSON.parse(localStorage.getItem('currentAppointment') || '{}') as any;
    this.setCdmPatientContext(currentAppointment, appointmentInfo, historyAppointment);

    if (appointmentInfo?.practitioner?.referenceId) {
      this.userPreferenceService.getSoapSettings(appointmentInfo?.practitioner?.referenceId, CVA_CONSTANTS.CONSTS.user_preference)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .subscribe((res: any) => {
          this.patientConsentSetting = res?.preference?.patient_consent;
          this.patientHistory = res?.preference?.patient_history || 0;
        })
    }

    this.preloadCdmSourceData();

  }

  openBottomSheet(): void {
    if (this.isBottomSheetEnabled) {
      this.bottomSheet.open(BottomSheetComponent, {
        panelClass: 'custom-bottom-sheet',
        hasBackdrop: false, // No backdrop to keep the sheet always visible
        closeOnNavigation: true
      });
    }
  }

  closeBottomSheet(): void {
    this.bottomSheet.dismiss();
  }

  isMobileView(): boolean {
    return window.innerWidth <= 767;
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isMobileView() && !this.hideLeftSection && !this.isLIC) {
      this.openBottomSheet();
    } else {
      this.closeBottomSheet();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isTabActive']) {
      this.recordingStarted = false;
      this.setFocusToEnd();
      this.stopRecording();
      this.isTabActive = true;
    } else if (!changes['transcription']) {
      this.stopRecording();
    }

    if (changes['patientId'] && changes['patientId'].currentValue !== changes['patientId'].previousValue) {
      this.preloadCdmSourceData();
    }

    const apptChange = changes['appointmentId'] ?? changes['appointmentRefId'];
    if (apptChange && !apptChange.firstChange && apptChange.currentValue !== apptChange.previousValue) {
      this.stopRecording();
      this.timerService.resetTimer();
      this.hasNewLineAppended = false;
      this.lineBreakPattern = '';
    }
  }

  private preloadCdmSourceData(): void {
    const currentPatientId = (this.patientId || '').trim();
    if (!currentPatientId) {
      return;
    }

    if (this.cdmLoadedPatientId === currentPatientId) {
      return;
    }

    this.isCdmSourceLoading = true;
    this.cdmPlanService.loadSourceData$(currentPatientId).subscribe({
      next: (sourceData) => {
        this.cdmSourceData = sourceData;
        this.cdmLoadedPatientId = currentPatientId;
        this.isCdmSourceLoading = false;
      },
      error: () => {
        this.cdmSourceData = {
          consultations: [],
          allergies: [],
          medications: [],
        };
        this.isCdmSourceLoading = false;
      },
    });
  }

  private setCdmPatientContext(...sources: Array<Partial<IAppointment> | null | undefined>): void {
    const validSources = sources.filter((source): source is Partial<IAppointment> => !!source);

    const getFirstDefinedValue = (resolver: (source: Partial<IAppointment>) => string | undefined): string => {
      for (const source of validSources) {
        const value = (resolver(source) || '').trim();
        if (value) {
          return value;
        }
      }
      return '';
    };

    const patientNameFromNested = getFirstDefinedValue((source) => {
      const firstName = source.patient?.firstName || '';
      const lastName = source.patient?.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName;
    });

    const patientNameFromFlat = getFirstDefinedValue((source) => {
      const flatSource = source as unknown as { name?: string; patientName?: string };
      return flatSource.name || flatSource.patientName;
    });

    this.cdmPatientName = patientNameFromNested || patientNameFromFlat;
    this.cdmPatientDob = getFirstDefinedValue((source) => source.patient?.dateOfBirth);
    this.cdmPatientMrn = getFirstDefinedValue((source) => source.patient?.medicalRecordNumber);
    this.cdmPatientGender = getFirstDefinedValue((source) => source.patient?.gender);
    this.cdmPatientId = getFirstDefinedValue((source) => source.patient?.referenceId || source.patient?.uid || source.patientId)
      || (this.patientId || '');
  }

  private mapCdmResponseToTable(response: unknown): void {
    const mapped = this.cdmPlanService.mapResponseToTable(response);
    this.cdmEligibility = mapped.eligibility;
    this.cdmEligibilityReason = mapped.eligibilityReason;
    this.cdmAnnualSmartGoal = mapped.annualSmartGoal;
    this.cdmPlanRows = mapped.planRows;
    this.cdmHealthScreeningRows = mapped.healthScreeningRows;
  }

  openCdmModal(templateRef: TemplateRef<unknown>): void {
    if (this.isCdmPlanLoading) {
      return;
    }

    if (this.isCdmSourceLoading) {
      this.cvaNotificationService.showNotification(
        'Please wait. Historical data is still loading for CDM plan generation.',
        NotificationClassEnum.INFO
      );
      return;
    }

    this.isCdmPlanLoading = true;
    this.cdmPlanError = '';
    this.cdmPlanResponse = null;
    this.cdmEligibility = '';
    this.cdmEligibilityReason = '';
    this.cdmAnnualSmartGoal = '—';
    this.cdmPlanRows = [];
    this.cdmHealthScreeningRows = [];

    this.dialog.open(templateRef, {
      width: '920px',
      maxWidth: '96vw',
      maxHeight: '88vh',
      panelClass: 'custom-modalbox',
      autoFocus: false,
    });

    const payload = this.cdmPlanService.buildPayload(this.cdmSourceData, (this.transcription || '').trim());
    this.historicalDataService.generateCdmPlan$(payload).subscribe({
      next: (response) => {
        this.cdmPlanResponse = response;
        this.mapCdmResponseToTable(response);
        this.isCdmPlanLoading = false;
      },
      error: () => {
        this.cdmPlanError = 'Failed to generate CDM plan. Please try again.';
        this.isCdmPlanLoading = false;
      },
    });
  }

  onTextChange(newValue: string) {
    this.transcription = newValue;
    this.transcriptionChange.emit(this.transcription);
  }

  getTranscription() {
    return this.transcription;
  }

  onSpeechStatus(status: 'listening' | 'processing' | '') {
    this.sttUIStatus = status;
    if (status) {
      setTimeout(() => this.scrollToBottom(), 0);
    }

    if (status === '') {
      setTimeout(() => {
        this.recordingStarted = false;
        this.populateNotesOptions();
      }, 500);
    }
  }

  populateNotesOptions() {
    //show the notes listing when live transcription is disabled
    if (this.transcription && !this.liveTranscriptionEnabled) {
      this.generateMoreBtn?.generateMore();
    }
  }

  get hasText(): boolean {
    return !!this.transcription?.trim();
  }

  /** Actions off when there is no text, or audio-chunks is still listening/processing. */
  get actionsDisabled(): boolean {
    const chunksBusy = this.selectedSpeechProvider === 'audio-chunks' && !!this.sttUIStatus; // '' and undefined both mean "idle"
    return !this.hasText || chunksBusy;
  }

  /**
   * Sets the speech response, handling interim results.
   * If no new line has been appended, adds the current time and appends the text.
   * If a new line has been appended, replaces the last appended result.
   * Scrolls to the bottom of the textarea.
   * @param text The speech text to set as the response
   */
  setTempSpeechResponse(text: string) {
    if (!this.hasNewLineAppended) {
      const now = this.timerService.getElapsedTimeFormatted();
      //set the line pattern, add 2 new lines only if transcription have existing result
      this.lineBreakPattern = this.transcription?.length ? `\n\n${now}\n` : `${now}\n`;
      this.transcription += `${this.lineBreakPattern}${text}`;
      this.hasNewLineAppended = true
    } else if (this.recordingStarted) {
      this.transcription = this.replaceLastAppendedResult(text);
    }
    this.scrollToBottom();
  }

  /**
   * Sets the final speech response, replacing the last appended result.
   * Resets the new line appended flag to false.
   * Scrolls to the bottom of the textarea.
   * @param text The final speech text to set as the response.
   */
  setFinalSpeechResponse(text: string) {
    if (this.selectedSpeechProvider === 'audio-chunks') {
      this.onTextChange(this.scribeService.mergeChunkText(this.transcription, text));
    } else {
      this.onTextChange(this.replaceLastAppendedResult(text));
    }

    this.hasNewLineAppended = false;
    this.scrollToBottom();
  }

  /**
   * Replaces the last appended result in transcription with the provided text.
   * Splits transcription by line break pattern and updates the last line with text.
   * Joins the lines back together using the line break pattern.
   * @param text The text to replace the last appended result with.
   * @returns The updated transcription with the last appended result replaced.
  */
  replaceLastAppendedResult(text: string): string {
    const lines = this.transcription.split(this.lineBreakPattern);
    if (lines.length) {
      // Replace the last line (last appended result) with the new text
      lines[lines.length - 1] = text;
    }
    return lines.join(this.lineBreakPattern);
  }

  scrollToBottom() {
    const el = this.textareaRef?.nativeElement;
    if (!el) return;
    const wasAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (!wasAtBottom) return;
    this.cdr.detectChanges();
    Promise.resolve().then(() => {
      el.scrollTop = el.scrollHeight;
    });
  }

  recordedText(text: string) {
    this.toggleTabDisable.emit(false);
    if (text && this.isTabActive && this.recordingStarted) {
      this.setFinalSpeechResponse(text);
    }
  }

  recognizingText(text: string) {
    this.toggleTabDisable.emit(true);
    if (text && this.isTabActive && this.recordingStarted) {
      this.setTempSpeechResponse(text);
    }
  }

  /**
   * switching back for edit
   * when user clicks on mic icon 
   */
  goToEditMode() {
    this.switchToEditMode.emit(true);
  }

  setFocusToEnd() {
    setTimeout(() => {
      const textarea: HTMLTextAreaElement = this.textareaRef?.nativeElement;

      if (textarea && this.liveTranscriptionEnabled !== false) {
        textarea.setSelectionRange(textarea.value.length, textarea.value.length, 'forward');
        textarea.focus();
      }
    });
  }

  // Gate used by the STT control before telehealth guide / recording starts.
  ensurePatientConsent = async (): Promise<boolean> => {
    const disableConsent = localStorage.getItem('disablePatientConsent');
    if (disableConsent === 'true') return true;
    if (!this.patientConsentSetting) return true;

    const localConsent = localStorage.getItem('patientConsent');
    if (this.userConsentTaken || localConsent) return true;

    const userRes = await firstValueFrom(this.consentService.showPopup());
    if (!userRes) return false;

    localStorage.setItem('patientConsent', 'true');
    this.appointmentService.updatePatientConsent(this.appointmentId).subscribe();
    return true;
  };

  async checkStartAndStop(event: string) {
    console.log("inside checkStartAndStop .. " + event)
    if (event === 'started') {
      // Consent already ran via preStartCheck before guide / capture.
      this.startRecording();
    } else if (event === 'stopped') {
      this.sessionInactivityService.setSpeechInProgress(false);
      this.timerService.pauseTimer();
      if (this.selectedSpeechProvider === 'azure') {
        this.recordingStarted = false;
        // this.populateNotesOptions();
      }
      this.toggleTabDisable.emit(false);
      this.recordingStateChange.emit(false);

    }
  }

  startRecording() {
    console.log("startRecording ..............");
    this.userConsentTaken = true;
    this.sessionInactivityService.setSpeechInProgress(true);
    this.recordingStarted = true;
    this.recordingStateChange.emit(true);
    this.timerService.resumeTimer();
    console.log("Recording started");
  }

  stopRecording() {
    console.log("Recording stopped");
    if (this.selectedSpeechProvider === 'azure') {
      this.recordingStarted = false;
    }

    this.recordingStateChange.emit(false);

    if (this.selectedSpeechProvider === 'azure') {
      this.speechToTextService.stopRec.next('stopped');
    } else if (this.selectedSpeechProvider === 'audio-chunks') {
      this.scribeService.stopRec.next('stopped');
    }
    this.timerService.pauseTimer();
  }

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  mergeTabsAndData(tabs: ITab[], tabData: any) {
    return tabs.map(tab => {
      const identifier = tab.identifier;
      const userInputKey = `${tab.identifier}_user_input`;
      return {
        ...tab,
        content: tabData[identifier] ?? '',
        userInput: tabData[userInputKey] ?? []
      };
    });
  }

  save() {
    let tabsData = null;
    if (this.transcription?.trim().length > 0) {
      this.stopRecording();
      console.log("Saving recorded transcription to DB");
      const rawTabs = localStorage.getItem("tabs");
      const tabs = rawTabs?.trim().startsWith("[") ? JSON.parse(rawTabs) : [];
      if (tabs?.length && tabs.length > 1) {
        const notesData = JSON.parse(localStorage.getItem("consultationForm") ?? '');
        tabsData = this.mergeTabsAndData(tabs, notesData);
      }

      const recommendations = this.consultationRecommendationService.getRecommendations();

      this.appointmentService.updateTranscription(
        this.appointmentRefId,
        this.transcription.trim(),
        tabsData,
        recommendations
      )
        .subscribe({
          next: () => {
            console.log("Recorded transcription saved to DB");
            this.cvaNotificationService.showNotification(
              CVA_CONSTANTS.MESSAGE.TRANSCRIPTION_UPDATED,
              NotificationClassEnum.SUCCESS
            );
            if (!localStorage.getItem('isLIC')) {
              this.router.navigate([ROUTE_CONSTANTS.PATIENT_DASHBOARD]);
            }
          },
          error: () => {
            console.log("Recorded transcription was not saved to DB");
            this.cvaNotificationService.showNotification(
              CVA_CONSTANTS.MESSAGE.save_err_soap_notes,
              NotificationClassEnum.ERROR
            );
            console.error(CVA_CONSTANTS.MESSAGE.save_err_soap_notes)
          },
        });
    }
  }

  generate(addNewTab: ITab) {
    this.updateTabIndex.emit(addNewTab)
  }

  generateMore(addNewTab: ITab) {
    this.updateTabIndex.emit(addNewTab)
  }

  reDirectToSetting() {
    this.router.navigate([ROUTE_CONSTANTS.SOAP_CONFIGURATION]);
  }

  startFromOverlay() {
    this.speechHost?.nativeElement
      .querySelector<HTMLButtonElement>('#startRecording, #stopRecording')
      ?.click();
  }

  get overlayStatus(): SttUIStatus {
    return resolveOverlayStatus(this.selectedSpeechProvider, this.recordingStarted, this.sttUIStatus);
  }

  ngOnDestroy(): void {
    this.stopRecording();
    this.timerService.resetTimer();
    this.closeBottomSheet();
  }
}
